import React, { useState, useEffect } from 'react';
import { useCouple } from '../../contexts/CoupleContext';
import AdminLayout from '../../components/layouts/AdminLayout';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import Alert from '../../components/common/Alert';
import Loader from '../../components/common/Loader';
import Input from '../../components/common/Input';
import { DollarSign, Heart, Link as LinkIcon, Edit2, CreditCard, X } from 'react-feather';
import PaymentService from '../../services/payment.service';
import { Contribution } from '../../types';
import {
  formatPrice,
  formatPriceSimple,
  SupportedCurrency,
  CURRENCY_SYMBOLS,
  getAmountInMinorUnit,
} from '../../utils/currency';
import BalanceService from '../../services/balance.service';

// Extended Contribution interface with optional amountProcessed flag
interface EnhancedContribution extends Contribution {
  amountProcessed?: boolean;
}

// Define a proper interface for the contribution summary
interface ContributionSummary {
  totalAmount: number;
  totalCount: number;
  currency?: string;
}

const WeddingFund: React.FC = () => {
  const { currentCouple, updateCouple } = useCouple();

  const [contributions, setContributions] = useState<EnhancedContribution[]>([]);
  const [summary, setSummary] = useState<ContributionSummary>({
    totalAmount: 0,
    totalCount: 0,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Goal editing state
  const [isEditingGoal, setIsEditingGoal] = useState<boolean>(false);
  const [fundingGoal, setFundingGoal] = useState<string>('0');
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Withdrawal state
  const [showWithdrawalModal, setShowWithdrawalModal] = useState<boolean>(false);
  const [withdrawalAmount, setWithdrawalAmount] = useState<string>('');
  const [bankCode, setBankCode] = useState<string>('');
  const [accountNumber, setAccountNumber] = useState<string>('');
  const [accountName, setAccountName] = useState<string>('');
  const [isProcessingWithdrawal, setIsProcessingWithdrawal] = useState<boolean>(false);

  // Currency for display
  const [currency, setCurrency] = useState<SupportedCurrency>('NGN');

  // Simplified bank list (fetch from Paystack /bank in production)
  const banks = [
    { code: '044', name: 'Access Bank' },
    { code: '063', name: 'Diamond Bank' },
    { code: '035', name: 'GTBank' },
  ];

  useEffect(() => {
    if (currentCouple) {
      fetchData();
      setFundingGoal(currentCouple.fundingGoal?.toString() || '0');

      // Set preferred currency if available
      if (
        currentCouple.preferredCurrency &&
        ['NGN', 'USD', 'GHS', 'KES', 'ZAR', 'XOF'].includes(currentCouple.preferredCurrency)
      ) {
        setCurrency(currentCouple.preferredCurrency as SupportedCurrency);
      }
    }
  }, [currentCouple]);

  // Convert minor units (like kobo) to major units (like naira)
  const convertToMajorUnits = (amount: number, currencyCode?: string): number => {
    // If the amount seems too large (suggesting it's in minor units)
    // then divide by 100 to convert to major units
    if (amount > 1000) {
      return amount / 100;
    }
    return amount;
  };

  const fetchData = async () => {
    if (!currentCouple) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch contributions and summary in parallel
      const [contributionsData, summaryData] = await Promise.all([
        PaymentService.getCoupleContributions(currentCouple._id),
        PaymentService.getContributionSummary(currentCouple._id),
      ]);

      // Process contributions data to ensure amounts are in major units
      const processedContributions = contributionsData.map((contribution) => {
        if (
          contribution.amount > 1000 &&
          !(contribution as EnhancedContribution).amountProcessed
        ) {
          return {
            ...contribution,
            amount: convertToMajorUnits(contribution.amount, contribution.currency),
            amountProcessed: true,
          };
        }
        return contribution;
      });

      // Fix summary total if needed (convert from minor units if very large)
      const processedSummary: ContributionSummary = { ...summaryData };
      if (processedSummary.totalAmount > 10000) {
        processedSummary.totalAmount = convertToMajorUnits(
          processedSummary.totalAmount,
          processedSummary.currency
        );
      }

      setContributions(processedContributions);
      setSummary(processedSummary);

      console.log('Processed summary:', processedSummary);
      console.log('Raw summary from API:', summaryData);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load wedding fund data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGoal = async () => {
    if (!currentCouple) return;

    setIsSaving(true);

    try {
      await updateCouple(currentCouple._id, {
        fundingGoal: Number(fundingGoal),
      });

      setIsEditingGoal(false);
      setSuccess('Funding goal updated successfully');

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to update funding goal';
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const copyFundLink = () => {
    if (!currentCouple) return;

    const url = `${window.location.origin}/wedding/${currentCouple.customUrl}/registry`;
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setSuccess('Registry link copied to clipboard!');
        setTimeout(() => {
          setSuccess(null);
        }, 3000);
      })
      .catch(() => {
        alert('Failed to copy link. Please copy manually: ' + url);
      });
  };

  const handleWithdrawalRequest = async () => {
    // Client-side validation
    if (!withdrawalAmount || Number(withdrawalAmount) <= 0) {
      setError('Please enter a valid withdrawal amount');
      return;
    }
    
    if (Number(withdrawalAmount) > summary.totalAmount) {
      setError('Withdrawal amount cannot exceed your available balance');
      return;
    }
    
    if (!bankCode || !accountNumber || !accountName) {
      setError('Please provide all bank details');
      return;
    }
    
    setIsProcessingWithdrawal(true);
    setError(null);
    const bankDetails = {
      bank_code: bankCode,
      account_number: accountNumber,
      account_name: accountName
    };
    
    
    try {
      // Create withdrawal request with proper format matching the WithdrawalRequest interface
      const withdrawalRequest = {
        amount: Number(withdrawalAmount),
        accountDetails: JSON.stringify(bankDetails),
        notes: 'Withdrawal from wedding fund',
        currency: currency as SupportedCurrency,
      };
      
      console.log('Sending withdrawal request:', withdrawalRequest);
      
      const response = await BalanceService.initiateWithdrawal(withdrawalRequest);
      
      if (response.success) {
        setSuccess(
          `Your withdrawal request for ${formatPriceSimple(
            Number(withdrawalAmount),
            currency
          )} has been submitted successfully. ${response.message || ''}`
        );
        setShowWithdrawalModal(false);
        resetWithdrawalForm();
        await fetchData();
      } else {
        throw new Error(response.message || 'Failed to process withdrawal request');
      }
    } catch (err) {
      console.error('Withdrawal error details:', err);
      
      // Extract the most useful error message
      let errorMessage = 'Failed to process withdrawal request';
      
      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'object' && err !== null) {
        const errorObj = err as any;
        if (errorObj.response?.data?.message) {
          errorMessage = errorObj.response.data.message;
        } else if (errorObj.message) {
          errorMessage = errorObj.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsProcessingWithdrawal(false);
    }
  };

  const resetWithdrawalForm = () => {
    setWithdrawalAmount('');
    setBankCode('');
    setAccountNumber('');
    setAccountName('');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getCurrencySymbol = (currencyCode?: string) => {
    const code = currencyCode || currency || 'NGN';
    return CURRENCY_SYMBOLS[code as SupportedCurrency] || '₦';
  };

  if (!currentCouple) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full py-20">
          <p className="text-gray-500">Please select a couple from the sidebar</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                Wedding Fund
              </h2>
            </div>
            <div className="mt-4 flex md:mt-0 md:ml-4 space-x-3">
              <Button
                onClick={copyFundLink}
                variant="outline"
                leftIcon={<LinkIcon className="h-4 w-4" />}
                customClasses="border-pink-300 text-pink-600 hover:bg-pink-50 dark:border-pink-400 dark:text-pink-200 dark:hover:bg-pink-900"
              >
                Copy Registry Link
              </Button>
              {summary.totalAmount > 0 && (
                <Button
                  onClick={() => setShowWithdrawalModal(true)}
                  leftIcon={<CreditCard className="h-4 w-4" />}
                  customClasses="bg-pink-500 hover:bg-pink-600 text-white"
                >
                  Withdraw Funds
                </Button>
              )}
            </div>
          </div>

          {error && (
            <Alert
              type="error"
              message={error}
              dismissible
              onDismiss={() => setError(null)}
              className="mt-4"
            />
          )}

          {success && (
            <Alert
              type="success"
              message={success}
              dismissible
              onDismiss={() => setSuccess(null)}
              className="mt-4"
            />
          )}

          {loading ? (
            <div className="py-12 flex justify-center">
              <Loader size="lg" label="Loading wedding fund data..." />
            </div>
          ) : (
            <>
              {/* Dashboard Cards */}
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Card className="bg-gradient-to-br from-pink-400 to-pink-500 text-white dark:from-pink-500 dark:to-pink-600">
                  <div className="flex items-center">
                    <div className="p-3 rounded-md bg-pink-500 bg-opacity-50">
                      <DollarSign className="h-6 w-6 text-white" />
                    </div>
                    <div className="ml-5">
                      <h3 className="text-lg font-medium">Total Contributions</h3>
                      <div className="mt-1 flex items-baseline">
                        <p className="text-3xl font-semibold">
                          {formatPriceSimple(
                            summary.totalAmount,
                            summary.currency as SupportedCurrency || currency
                          )}
                        </p>
                        <p className="ml-2 text-sm">
                          from {summary.totalCount}{' '}
                          {summary.totalCount === 1 ? 'contribution' : 'contributions'}
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="p-3 rounded-md bg-pink-50 dark:bg-pink-900">
                        <Heart className="h-6 w-6 text-pink-500 dark:text-pink-300" />
                      </div>
                      <div className="ml-5">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                          Funding Goal
                        </h3>
                        {!isEditingGoal ? (
                          <div className="mt-1 flex items-baseline">
                            <p className="text-3xl font-semibold text-gray-900 dark:text-white">
                              {formatPriceSimple(currentCouple.fundingGoal || 0, currency)}
                            </p>
                            <button
                              onClick={() => setIsEditingGoal(true)}
                              className="ml-2 text-pink-500 hover:text-pink-600 dark:text-pink-300 dark:hover:text-pink-200"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="mt-2 flex items-center">
                            <div className="relative rounded-md shadow-sm">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <span className="text-gray-500 sm:text-sm dark:text-gray-400">
                                  {getCurrencySymbol()}
                                </span>
                              </div>
                              <input
                                type="number"
                                value={fundingGoal}
                                onChange={(e) => setFundingGoal(e.target.value)}
                                className="focus:ring-pink-500 focus:border-pink-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                                min="0"
                                step="1"
                              />
                            </div>
                            <Button
                              className="ml-3"
                              size="sm"
                              loading={isSaving}
                              onClick={handleSaveGoal}
                              customClasses="bg-pink-500 hover:bg-pink-600 text-white dark:bg-pink-600 dark:hover:bg-pink-700 dark:text-white"
                            >
                              Save
                            </Button>
                            <Button
                              className="ml-2"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setIsEditingGoal(false);
                                setFundingGoal(currentCouple.fundingGoal?.toString() || '0');
                              }}
                              customClasses="border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                              Cancel
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>

                    {!isEditingGoal && currentCouple.fundingGoal && summary.totalAmount > 0 && (
                      <div className="ml-2 h-12 w-12 relative">
                        <svg
                          viewBox="0 0 36 36"
                          className="h-12 w-12 stroke-current text-pink-100 dark:text-pink-900"
                        >
                          <path
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            strokeWidth="3"
                            strokeDasharray="100, 100"
                          />
                        </svg>
                        <svg
                          viewBox="0 0 36 36"
                          className="absolute top-0 left-0 h-12 w-12 stroke-current text-pink-500 dark:text-pink-400"
                        >
                          <path
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            strokeWidth="3"
                            strokeDasharray={`${Math.min(
                              100,
                              (summary.totalAmount / currentCouple.fundingGoal) * 100
                            )}, 100`}
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center text-xs font-medium text-pink-600 dark:text-pink-300">
                          {Math.round((summary.totalAmount / currentCouple.fundingGoal) * 100)}%
                        </div>
                      </div>
                    )}
                  </div>

                  {currentCouple.fundingGoal && summary.totalAmount > 0 && !isEditingGoal && (
                    <div className="mt-4 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                      <div
                        className="bg-pink-500 dark:bg-pink-400 h-2.5 rounded-full"
                        style={{
                          width: `${Math.min(
                            100,
                            (summary.totalAmount / currentCouple.fundingGoal) * 100
                          )}%`,
                        }}
                      ></div>
                    </div>
                  )}
                </Card>
              </div>

              {/* Recent Contributions */}
              <div className="mt-8">
                <Card title="Recent Contributions">
                  {contributions.length === 0 ? (
                    <div className="py-6 text-center">
                      <DollarSign className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                        No contributions yet
                      </h3>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Share your wedding registry to receive contributions for your wedding fund.
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-pink-50 dark:bg-pink-900">
                          <tr>
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-pink-600 dark:text-pink-300 uppercase tracking-wider"
                            >
                              Contributor
                            </th>
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-pink-600 dark:text-pink-300 uppercase tracking-wider"
                            >
                              Amount
                            </th>
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-pink-600 dark:text-pink-300 uppercase tracking-wider"
                            >
                              Date
                            </th>
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-pink-600 dark:text-pink-300 uppercase tracking-wider"
                            >
                              Message
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {contributions.map((contribution) => (
                            <tr key={contribution._id}>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {contribution.contributorName}
                                </div>
                                {contribution.contributorEmail && (
                                  <div className="text-sm text-gray-500 dark:text-gray-400">
                                    {contribution.contributorEmail}
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {formatPriceSimple(
                                    contribution.amount,
                                    contribution.currency as SupportedCurrency || currency
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {formatDate(contribution.createdAt)}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                {contribution.message ? (
                                  <div className="text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                                    {contribution.message}
                                  </div>
                                ) : (
                                  <div className="text-sm text-gray-400 dark:text-gray-500 italic">
                                    No message
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Withdrawal Modal */}
      {showWithdrawalModal && (
        <div className="fixed inset-0 overflow-y-auto z-50">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              ​
            </span>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  onClick={() => setShowWithdrawalModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-pink-100 dark:bg-pink-900 sm:mx-0 sm:h-10 sm:w-10">
                    <CreditCard className="h-6 w-6 text-pink-600 dark:text-pink-300" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                      Withdraw Funds
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Available balance:{' '}
                        {formatPriceSimple(
                          summary.totalAmount,
                          summary.currency as SupportedCurrency || currency
                        )}
                      </p>
                    </div>

                    <div className="mt-4 space-y-4">
                      <div>
                        <label
                          htmlFor="withdrawalAmount"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Withdrawal Amount
                        </label>
                        <div className="mt-1 relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 sm:text-sm">
                              {getCurrencySymbol(summary.currency)}
                            </span>
                          </div>
                          <input
                            type="number"
                            id="withdrawalAmount"
                            value={withdrawalAmount}
                            onChange={(e) => setWithdrawalAmount(e.target.value)}
                            max={summary.totalAmount}
                            min="0"
                            className="focus:ring-pink-500 focus:border-pink-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            placeholder="0.00"
                          />
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="bankCode"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Bank
                        </label>
                        <select
                          id="bankCode"
                          value={bankCode}
                          onChange={(e) => setBankCode(e.target.value)}
                          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        >
                          <option value="">Select Bank</option>
                          {banks.map((bank) => (
                            <option key={bank.code} value={bank.code}>
                              {bank.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label
                          htmlFor="accountNumber"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Account Number
                        </label>
                        <input
                          type="text"
                          id="accountNumber"
                          value={accountNumber}
                          onChange={(e) => setAccountNumber(e.target.value)}
                          className="mt-1 focus:ring-pink-500 focus:border-pink-500 block w-full sm:text-sm border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          placeholder="Enter 10-digit account number"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="accountName"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Account Name
                        </label>
                        <input
                          type="text"
                          id="accountName"
                          value={accountName}
                          onChange={(e) => setAccountName(e.target.value)}
                          className="mt-1 focus:ring-pink-500 focus:border-pink-500 block w-full sm:text-sm border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          placeholder="Enter account holder name"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <Button
                  onClick={handleWithdrawalRequest}
                  loading={isProcessingWithdrawal}
                  disabled={!withdrawalAmount || !bankCode || !accountNumber || !accountName}
                  customClasses="bg-pink-600 hover:bg-pink-700 text-white sm:ml-3"
                >
                  Request Withdrawal
                </Button>
                <Button
                  onClick={() => setShowWithdrawalModal(false)}
                  variant="outline"
                  customClasses="border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600 mt-3 sm:mt-0"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default WeddingFund;